import Client from '@/api/client'
import { getConfig } from '@/app/config'

import { ManualQuote, ManualQuoteStatus } from '../domain/manual-quote'

const client = new Client(getConfig().API_ENDPOINT ?? '')

type ManualQuotesSearchResult = ManualQuote[]

export const getManualQuotes = async (): Promise<ManualQuote[]> => {
  return await client.get<ManualQuotesSearchResult>('/api/shipping/api/v1/quotes/manualQuotes')
}

export const withdrawManualQuote = async ({ id, customerId }: { id: string; customerId: string }): Promise<void> => {
  return await client.put<void>(`/api/quotation/manualQuotes/${id}/withdraw?customerId=${customerId}`)
}

export enum ManualQuoteListFilterType {
  ALL = 'all',
  PENDING = 'pending',
  VALID = 'valid',
  EXPIRED = 'expired',
  DECLINED = 'declined',
  NOT_AVAILABLE = 'notAvailable',
  WITHDRAWN = 'withdrawn',
}

export const getFilters = () => [
  { name: ManualQuoteListFilterType.ALL, label: 'All' },
  {
    name: ManualQuoteListFilterType.PENDING,
    label: 'Pending',
  },
  { name: ManualQuoteListFilterType.VALID, label: 'Valid' },
  {
    name: ManualQuoteListFilterType.EXPIRED,
    label: 'Expired',
  },
  {
    name: ManualQuoteListFilterType.DECLINED,
    label: 'Declined',
  },
  {
    name: ManualQuoteListFilterType.WITHDRAWN,
    label: 'Withdrawn',
  },
  {
    name: ManualQuoteListFilterType.NOT_AVAILABLE,
    label: 'Not available',
  },
]

export const isQuoteSheetPending = (quoteSheet: ManualQuote): boolean => {
  const allowedStatuses: ManualQuoteStatus[] = ['open', 'pending', 'requested']

  return allowedStatuses.some((item) => item === quoteSheet.request.manualQuoteRequest?.status)
}

export const isQuoteSheetValid = (quoteSheet: ManualQuote): boolean => {
  const allowedStatuses: ManualQuoteStatus[] = ['ready', 'booked', 'sent']

  return (
    allowedStatuses.some((item) => item === quoteSheet.request.manualQuoteRequest?.status) &&
    new Date(quoteSheet.response.validUntil) > new Date()
  )
}

export const isQuoteSheetExpired = (quoteSheet: ManualQuote): boolean => {
  return quoteSheet.request.manualQuoteRequest?.status === 'expired'
}

export const isQuoteSheetDeclined = (quoteSheet: ManualQuote): boolean => {
  return quoteSheet.request.manualQuoteRequest?.status === 'declinedByCustomer'
}

export const isQuoteSheetNotAvailable = (quoteSheet: ManualQuote): boolean => {
  return quoteSheet.request.manualQuoteRequest?.status === 'declinedByFreighthub'
}

export const isQuoteSheetWithdrawn = (quoteSheet: ManualQuote): boolean => {
  return quoteSheet.request.manualQuoteRequest?.status === 'withdrawn'
}

const filterMap: Record<ManualQuoteListFilterType, (quoteSheet: ManualQuote) => boolean> = {
  [ManualQuoteListFilterType.ALL]: () => true,
  [ManualQuoteListFilterType.PENDING]: isQuoteSheetPending,
  [ManualQuoteListFilterType.VALID]: isQuoteSheetValid,
  [ManualQuoteListFilterType.EXPIRED]: isQuoteSheetExpired,
  [ManualQuoteListFilterType.DECLINED]: isQuoteSheetDeclined,
  [ManualQuoteListFilterType.NOT_AVAILABLE]: isQuoteSheetNotAvailable,
  [ManualQuoteListFilterType.WITHDRAWN]: isQuoteSheetWithdrawn,
}

export const getFilteredQuoteSheetsByFilterType = (
  quoteSheets: ManualQuote[],
  filterType: ManualQuoteListFilterType
) => {
  return quoteSheets.filter(filterMap[filterType] || (() => true))
}
